import { type ZxcvbnResult, zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en'

// Mirrors Clerk dashboard `password_settings`. Keep these in sync — Clerk is
// the enforcement boundary; the client only uses them to drive UX (strength
// bar threshold + "X characters minimum" copy).
export const MIN_PASSWORD_LENGTH = 8

// 0-4 zxcvbn scale. Clerk's UI labels (Weak/Normal/Strong) map roughly to
// scores 1/2/3. Keep aligned with `min_zxcvbn_strength` in the Clerk dashboard
// so the strength bar's "green" threshold matches what Clerk accepts.
export const MIN_PASSWORD_SCORE = 2

let optionsConfigured = false

const ensureZxcvbnConfigured = () => {
  if (optionsConfigured) return
  zxcvbnOptions.setOptions({
    translations: zxcvbnEnPackage.translations,
    graphs: zxcvbnCommonPackage.adjacencyGraphs,
    dictionary: {
      ...zxcvbnCommonPackage.dictionary,
      ...zxcvbnEnPackage.dictionary,
    },
  })
  optionsConfigured = true
}

export interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4
  meetsLength: boolean
  meetsScore: boolean
  meetsAll: boolean
  warning: string
  suggestions: string[]
}

export const evaluatePassword = (password: string, userInputs: string[] = []): PasswordStrength => {
  if (!password) {
    return {
      score: 0,
      meetsLength: false,
      meetsScore: false,
      meetsAll: false,
      warning: '',
      suggestions: [],
    }
  }
  ensureZxcvbnConfigured()
  const result: ZxcvbnResult = zxcvbn(password, userInputs)
  const meetsLength = password.length >= MIN_PASSWORD_LENGTH
  const meetsScore = result.score >= MIN_PASSWORD_SCORE
  return {
    score: result.score,
    meetsLength,
    meetsScore,
    meetsAll: meetsLength && meetsScore,
    warning: result.feedback.warning ?? '',
    suggestions: result.feedback.suggestions ?? [],
  }
}
